import React, { useCallback, useMemo, useState } from 'react';
import NaVaForm from '../../../../common/forms/validation/na-va-form/NaVaForm';
import NaVaFormStatePuller from '../../../../common/forms/NaVaFormStatePuller';
import Switch from '../../../../common/forms/validation/controls/switch/Switch';
import { NaVaFormContextType, NaVaFormValues } from '../../../../common/forms/validation/na-va-form/types';
import { L } from '../../../../abp/utils';
import Wizard from '../../../../common/components/wizard/Wizard';
import { getExtensionValue } from '../../../../util/EntityUtils';
import _, { replace, template } from 'lodash';
import TemplateFieldManagement, { makeTemplateConfigIdentifier, TemplateFieldData, TemplateInputEditorField } from './TemplateFieldManagement';
import filter from '../../../../util/FilterUtils';
import PlmControl from '../plm-job-list/PlmControl';
import { defaultColumnConfig } from '../../data/columnConfigurations';
import { defaultPlmControlState, PlmControlSection } from '../plm-job-list/components/PlmControlComponents';
import { FIELD_ID } from '../../../../data/field-constants/GeneralConstants';
import { useNavigate } from 'react-router-dom';
import ExpansionPanel from '../../../../common/components/expansion-panel/ExpansionPanel';

const DataManagement: React.FC = () => {

    const [selectedTemplate, setSelectedTemplate] = useState();
    const handleTemplateSelected = useCallback((keys: string[], items: any[]) => {
        if (items.length > 0) {
            getExtensionValue(items[0], '')
            setSelectedTemplate(items[0]);
        } else {
            setSelectedTemplate(undefined);
        }
    }, []);

    const [jobId, setJobId] = useState<string | undefined>(undefined);
    const handleJobCreated = useCallback((jobId: string | undefined) => {
        setJobId(jobId);
    }, [setJobId]);

    const [templateData, setTemplateData] = useState<any>({});
    const handleTemplateDataChanged = useCallback((data: TemplateFieldData) => {
        setTemplateData(data);
    }, []);

    const fieldManagement = useMemo(() => {
        return <TemplateFieldManagement dataItem={selectedTemplate} onCreateJob={handleJobCreated} onTemplateDataChanged={handleTemplateDataChanged} />;
    }, [selectedTemplate, handleJobCreated, handleTemplateDataChanged]);

    const templateSelection = useMemo(() => {
        return <>
            <TemplateSelection handleSelectionChanged={handleTemplateSelected} />
            {/* {fieldManagement} */}
        </>
    }, [handleTemplateSelected/*, fieldManagement*/]);

    const completionStep = useMemo(() => {
        return jobId ? <CompletionStep jobId={jobId} templateFieldData={templateData} /> : null
    }, [jobId, templateData]);


    const steps = useMemo(() => [
        {
            label: "Vorlage auswählen",
            isValid: !!selectedTemplate,
            component: templateSelection,
            hideWhenInactive: true
        },
        {
            label: "Vorlagenfelder",
            disabled: !selectedTemplate,
            isValid: !!jobId,
            component: fieldManagement,
            hideWhenInactive: true
        },
        {
            label: "Abschließen",
            disabled: !jobId,
            isValid: !!jobId,
            component: completionStep
        }
    ], [selectedTemplate, templateSelection, fieldManagement, completionStep, jobId]);

    const navigate = useNavigate();

    return <div className="data-management my-3 px-4">
        {/* <h5>Stammdatenverwaltung</h5> */}

        <Wizard
            header=""
            steps={steps}
            completeAction={() => navigate('/app/template-management/job-management')}
        />

    </div >
};

const TemplateSelection: React.FC<{ handleSelectionChanged: (keys: string[], items: any[]) => void }> = ({ handleSelectionChanged }) => {

    const [{ values: filterValues }, setFormState] = useState<NaVaFormContextType<NaVaFormValues>>({} as any);

    const handleJobSelection = useCallback((selection: { section: PlmControlSection, keys?: string[], items?: any[] }[]) => {
        const section = selection.find(x => x.section === 'job');

        handleSelectionChanged(section?.keys ?? [], section?.items ?? []);

    }, [handleSelectionChanged])

    return <div className="template-selection">
        <div className="filters row my-3">
            <h5 className="mb-3">Vorlagenart:</h5>
            <NaVaForm
                initialValues={{ selectJobTemplates: true }}
                onSubmit={() => { }}>
                <NaVaFormStatePuller onStateChanged={setFormState} />
                <div className="d-flex align-items-center">
                    <h5 className="mx-3">{L('Item')}</h5>
                    <Switch name="selectJobTemplates" disabled={true} />
                    <h5 className="mx-3">{L('Job')}</h5>
                </div>
            </NaVaForm>
        </div>

        <h5 className="mb-3">Bitte wählen Sie die Vorlage aus, die Sie nutzen möchten:</h5>

        <PlmControl columnConfig={defaultColumnConfig} showTemplates onSelectionChanged={handleJobSelection} selectable readOnly={true} />
    </div>
}

const CompletionStep: React.FC<{ jobId: string, templateFieldData: TemplateFieldData }> = ({ jobId, templateFieldData }) => {

    const createdJobControlStateConfig = useMemo(() => {
        return {
            ...defaultPlmControlState,
            job: {
                ...defaultPlmControlState.job,
                filter: {
                    ...defaultPlmControlState.job.filter,
                    internalFilters: [...defaultPlmControlState.job.filter?.internalFilters ?? [], filter(FIELD_ID).contains(jobId)]
                }
            }
        }
    }, [jobId]);

    const { templateFields, groupedColoredTemplateFields, replacementValues } = templateFieldData;

    return <div className="completion">
        <h5>Abschließen des Vorgangs</h5>
        <div>Bitte überprüfen Sie die angelegten Daten und speichern Sie sie dann:</div>

        <NaVaForm initialValues={replacementValues} onSubmit={() => { }}>

            <ExpansionPanel initialExpanded={true} title={L('TemplateFields')}>
                <div className="row">
                    {templateFields?.map(f => {
                        return <TemplateInputEditorField config={f} key={makeTemplateConfigIdentifier(f)} groupedColoredTemplateFields={groupedColoredTemplateFields} disabled />
                    })}
                </div>
            </ExpansionPanel>
        </NaVaForm>

        <h5 className="mt-3">{L('Job')}</h5>
        <PlmControl type="Job" columnConfig={defaultColumnConfig} showTemplates={false} selectable={false} readOnly={true}
            controlStateConfig={createdJobControlStateConfig} listProps={{ enableAttachments: true }}>
        </PlmControl>

    </div>
}

export default DataManagement;